import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography,
  CircularProgress, Alert, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Chip,
  FormControlLabel, Checkbox, Divider
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  getDashboardTarjetas, getTarjetasDisponibles, guardarTarjetas,
  getAvisos, crearAviso, actualizarAviso, borrarAviso, getMisPermisos
} from '../api';

// Color por tarjeta según su naturaleza
const COLOR_TARJETA: any = {
  tareas: '#f59e0b',
  intervenciones: '#4f46e5',
  oportunidades: '#0ea5e9',
  ventas_facturar: '#22c55e',
  stock_bajo: '#ef4444',
  solicitudes_material: '#8b5cf6',
  ausencias_aprobar: '#ec4899',
  visitas: '#14b8a6',
};

const EMOJI_TARJETA: any = {
  tareas: '📋',
  intervenciones: '🔧',
  oportunidades: '💼',
  ventas_facturar: '🧾',
  stock_bajo: '📦',
  solicitudes_material: '🙋',
  ausencias_aprobar: '🌴',
  visitas: '🤝',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [tarjetas, setTarjetas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [avisos, setAvisos] = useState<any[]>([]);
  const [permisos, setPermisos] = useState<string[]>([]);
  const [avisoDialog, setAvisoDialog] = useState<any>(null);

  // Configuración de tarjetas
  const [configOpen, setConfigOpen] = useState(false);
  const [disponibles, setDisponibles] = useState<any[]>([]);
  const [seleccion, setSeleccion] = useState<string[]>([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const esAdmin = user.rol === 'admin';
  const puedePublicar = esAdmin || permisos.includes('publicar_avisos');

  const hora = new Date().getHours();
  const saludo = hora < 12 ? 'Buenos días' : hora < 20 ? 'Buenas tardes' : 'Buenas noches';
  const diasEs = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const mesesEs = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const hoy = new Date();
  const fechaStr = `${diasEs[hoy.getDay()]}, ${hoy.getDate()} de ${mesesEs[hoy.getMonth()]} de ${hoy.getFullYear()}`;

  const cargarTarjetas = () => getDashboardTarjetas().then(setTarjetas).catch(() => setTarjetas([]));
  const cargarAvisos = () => getAvisos().then(setAvisos).catch(() => setAvisos([]));

  useEffect(() => {
    Promise.all([cargarTarjetas(), cargarAvisos()]).finally(() => setLoading(false));
    if (!esAdmin) getMisPermisos().then(setPermisos).catch(() => setPermisos([]));
  }, []);

  // ── CONFIGURACIÓN DE TARJETAS ───────────────────────────
  const abrirConfig = async () => {
    try {
      const disp = await getTarjetasDisponibles();
      setDisponibles(disp);
      // Preseleccionar las que ya tiene
      setSeleccion(tarjetas.map(t => t.key));
      setConfigOpen(true);
    } catch {
      alert('Error cargando la configuración');
    }
  };

  const toggleTarjeta = (key: string) => {
    setSeleccion(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const guardarConfig = async () => {
    try {
      await guardarTarjetas(seleccion);
      setConfigOpen(false);
      cargarTarjetas();
    } catch {
      alert('Error al guardar la configuración');
    }
  };

  // ── AVISOS ──────────────────────────────────────────────
  const nuevoAviso = () => setAvisoDialog({ titulo: '', contenido: '', fecha_caducidad: '' });
  const guardarAviso = async () => {
    if (!avisoDialog.titulo || !avisoDialog.contenido) { alert('Título y contenido obligatorios'); return; }
    try {
      if (avisoDialog.id) await actualizarAviso(avisoDialog.id, avisoDialog);
      else await crearAviso(avisoDialog);
      setAvisoDialog(null);
      cargarAvisos();
    } catch (e: any) { alert(e.response?.data?.detail || 'Error al guardar'); }
  };
  const eliminarAviso = async (id: number) => {
    if (!window.confirm('¿Eliminar este aviso?')) return;
    try { await borrarAviso(id); cargarAvisos(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const formatFecha = (f: string) => {
    if (!f) return '';
    const d = new Date(f);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
  );

  return (
    <Box>
      {/* Cabecera */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>{saludo}, {user.nombre} 👋</Typography>
          <Typography variant="body2" color="text.secondary">{fechaStr}</Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<TuneIcon />} onClick={abrirConfig}>
          Configurar tarjetas
        </Button>
      </Box>

      {/* TARJETAS DINÁMICAS */}
      {tarjetas.length === 0 ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          No tienes tarjetas configuradas. Pulsa "Configurar tarjetas" para elegir las que quieres ver.
        </Alert>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {tarjetas.map(t => {
            const color = COLOR_TARJETA[t.key] || '#4f46e5';
            return (
              <Grid item xs={12} sm={6} md={3} key={t.key}>
                <Card sx={{ borderRadius: 3, height: '100%', cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 } }}
                  onClick={() => navigate(t.ruta)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {EMOJI_TARJETA[t.key]} {t.label}
                        </Typography>
                        <Typography variant="h3" sx={{ color, fontWeight: 600 }}>{t.valor}</Typography>
                      </Box>
                      <ArrowForwardIcon sx={{ color, opacity: 0.3 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* TABLÓN DE AVISOS */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CampaignIcon sx={{ color: '#F5C200' }} />
              <Typography variant="h6">Tablón de avisos</Typography>
            </Box>
            {puedePublicar && (
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={nuevoAviso} sx={{ bgcolor: '#4f46e5' }}>
                Publicar aviso
              </Button>
            )}
          </Box>

          {avisos.length === 0 ? (
            <Alert severity="info">No hay avisos publicados en este momento.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {avisos.map(a => {
                const puedeEditar = esAdmin || a.autor_id === user.id;
                return (
                  <Card key={a.id} variant="outlined" sx={{ borderRadius: 2, borderLeft: '4px solid #F5C200' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600 }}>{a.titulo}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {a.contenido}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              {a.autor_nombre} {a.autor_apellidos || ''} · {formatFecha(a.fecha_publicacion)}
                            </Typography>
                            {a.fecha_caducidad && (
                              <Chip label={`Hasta ${formatFecha(a.fecha_caducidad)}`} size="small"
                                variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                            )}
                          </Box>
                        </Box>
                        {puedeEditar && (
                          <Box sx={{ display: 'flex' }}>
                            <IconButton size="small" onClick={() => setAvisoDialog({ ...a, fecha_caducidad: a.fecha_caducidad || '' })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => eliminarAviso(a.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* DIALOG CONFIGURAR TARJETAS */}
      <Dialog open={configOpen} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setConfigOpen(false); } }} maxWidth="sm" fullWidth>
        <DialogTitle>⚙️ Configurar tarjetas del inicio</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Elige qué tarjetas quieres ver en tu pantalla de inicio. Si no seleccionas ninguna,
            se mostrarán automáticamente las principales según tus módulos.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Principales</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
            {disponibles.filter(d => d.principal).map(d => (
              <FormControlLabel key={d.key}
                control={<Checkbox checked={seleccion.includes(d.key)} onChange={() => toggleTarjeta(d.key)} />}
                label={`${EMOJI_TARJETA[d.key]} ${d.label}`} />
            ))}
          </Box>

          {disponibles.some(d => !d.principal) && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Opcionales</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {disponibles.filter(d => !d.principal).map(d => (
                  <FormControlLabel key={d.key}
                    control={<Checkbox checked={seleccion.includes(d.key)} onChange={() => toggleTarjeta(d.key)} />}
                    label={`${EMOJI_TARJETA[d.key]} ${d.label}`} />
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSeleccion([]); }}>Restablecer</Button>
          <Button onClick={() => setConfigOpen(false)}>Cancelar</Button>
          <Button onClick={guardarConfig} variant="contained" sx={{ bgcolor: '#4f46e5' }}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG CREAR / EDITAR AVISO */}
      <Dialog open={!!avisoDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setAvisoDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>{avisoDialog?.id ? 'Editar aviso' : 'Publicar aviso'}</DialogTitle>
        <DialogContent>
          {avisoDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Título *" value={avisoDialog.titulo}
                onChange={(e) => setAvisoDialog({ ...avisoDialog, titulo: e.target.value })} />
              <TextField label="Contenido *" multiline rows={4} value={avisoDialog.contenido}
                onChange={(e) => setAvisoDialog({ ...avisoDialog, contenido: e.target.value })} />
              <TextField label="Fecha de caducidad (opcional)" type="date" InputLabelProps={{ shrink: true }}
                value={avisoDialog.fecha_caducidad || ''}
                onChange={(e) => setAvisoDialog({ ...avisoDialog, fecha_caducidad: e.target.value })}
                helperText="Si la dejas vacía, el aviso será permanente" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvisoDialog(null)}>Cancelar</Button>
          <Button onClick={guardarAviso} variant="contained" sx={{ bgcolor: '#4f46e5' }}
            disabled={!avisoDialog?.titulo || !avisoDialog?.contenido}>
            {avisoDialog?.id ? 'Guardar' : 'Publicar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}